import { buildLoginRedirectPath, destinationForAuthRedirect, rememberPostLoginPath } from '../utils/authRedirect';

/** Renew this long before access-token exp. Production access tokens last 60 minutes. */
export const ACCESS_TOKEN_REFRESH_LEAD_SECONDS = 60;

let refreshFailureRedirected = false;

export function accessTokenRefreshDelayMs(
  expSeconds: number,
  nowMs: number,
  leadSeconds: number = ACCESS_TOKEN_REFRESH_LEAD_SECONDS,
): number {
  if (!Number.isFinite(expSeconds) || !Number.isFinite(nowMs)) return 0;
  const remainingMs = expSeconds * 1000 - nowMs;
  if (remainingMs <= 0) return 0;
  const leadMs = Math.max(0, leadSeconds * 1000);
  if (remainingMs > leadMs) return remainingMs - leadMs;
  // Already inside the lead window, or a short test token: still fire before exp.
  return Math.max(1, Math.floor(remainingMs / 2));
}

export function redirectToLoginAfterRefreshFailure(): boolean {
  if (refreshFailureRedirected) return false;
  if (typeof window === 'undefined') return false;
  const path = String(window.location?.pathname || '/');
  if (path === '/login' || path.startsWith('/login/')) return false;
  refreshFailureRedirected = true;
  const next = destinationForAuthRedirect(path, String(window.location?.search || ''));
  rememberPostLoginPath(next);
  const target = buildLoginRedirectPath(next);
  window.location.href = target;
  return true;
}

export function resetRefreshFailureRedirectForTests(): void {
  refreshFailureRedirected = false;
}

export type AccessTokenRefreshScheduler = {
  arm: (accessToken: string) => void;
  clear: () => void;
};

export function createAccessTokenRefreshScheduler(deps: {
  now?: () => number;
  leadSeconds?: number;
  getExp: (accessToken: string) => number | null;
  canRefresh: () => boolean;
  refresh: () => Promise<string | null>;
  onFailed: () => void;
}): AccessTokenRefreshScheduler {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let running = false;
  const now = deps.now ?? (() => Date.now());

  const clear = () => {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  };

  const arm = (accessToken: string) => {
    clear();
    if (!deps.canRefresh()) return;
    const exp = deps.getExp(accessToken);
    if (exp == null || !Number.isFinite(exp)) return;
    const delay = accessTokenRefreshDelayMs(exp, now(), deps.leadSeconds);
    timer = setTimeout(() => {
      timer = null;
      void run();
    }, delay);
  };

  const run = async () => {
    if (running) return;
    running = true;
    try {
      const next = await deps.refresh();
      if (!next) {
        clear();
        deps.onFailed();
      }
    } finally {
      running = false;
    }
  };

  return { arm, clear };
}
